import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpen, 
  Trophy, 
  Clock, 
  Star, 
  LogOut, 
  User, 
  Calendar,
  Target,
  Award,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';

interface Exam {
  id: number;
  title: string;
  description: string;
  duration_minutes: number;
  max_attempts: number;
  deadline: string;
  total_questions: number;
  question_count: number;
  canAttempt: boolean;
  currentAttempt?: any;
}

interface UserStats {
  exams_taken: number;
  total_attempts: number;
  average_score: number;
  best_score: number;
  total_points_earned: number;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [exams, setExams] = useState<Exam[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [examsResponse, statsResponse] = await Promise.all([
        axios.get('/test/available'),
        axios.get('/user/stats')
      ]);

      setExams(examsResponse.data.exams);
      setStats(statsResponse.data.stats);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError('Error cargando los datos');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExamStatus = (exam: Exam) => {
    if (!exam.canAttempt) {
      return { text: 'Completado', color: '#56ab2f', icon: '✅' };
    }
    if (exam.deadline && new Date(exam.deadline) < new Date()) {
      return { text: 'Expirado', color: '#ff416c', icon: '⏰' };
    }
    return { text: 'Disponible', color: '#4ecdc4', icon: '🎯' };
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Cargando tu aventura de inglés... 🌟
        </p>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="title"
      >
        <h1>
          <span className="emoji">👋</span>
          ¡Hola, {user?.username}!
          <span className="emoji">🌟</span>
        </h1>
        <h2>Tu Centro de Aventuras en Inglés</h2>
      </motion.div>

      {/* Barra de navegación */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
        style={{ marginBottom: '30px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <User style={{ color: '#4ecdc4' }} />
            <span style={{ fontWeight: 'bold', color: '#333' }}>
              {user?.isAdmin ? '👑 Administrador' : '🎓 Estudiante'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {user?.isAdmin && (
              <Link to="/admin" className="btn btn-secondary">
                Panel Admin
              </Link>
            )}
            <button onClick={logout} className="btn btn-danger">
              <LogOut style={{ marginRight: '5px' }} />
              Salir
            </button>
          </div>
        </div>
      </motion.div>

      {/* Estadísticas del usuario */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-4"
        >
          <div className="card" style={{ textAlign: 'center' }}>
            <Trophy style={{ fontSize: '2rem', color: '#ffd700', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.exams_taken || 0}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Exámenes Completados</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <Target style={{ fontSize: '2rem', color: '#ff6b6b', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {Math.round(stats.average_score || 0)}%
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Promedio</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <Star style={{ fontSize: '2rem', color: '#ffd700', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {Math.round(stats.best_score || 0)}%
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Mejor Puntuación</p>
          </div>

          <div className="card" style={{ textAlign: 'center' }}>
            <Award style={{ fontSize: '2rem', color: '#56ab2f', marginBottom: '10px' }} />
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4ecdc4' }}>
              {stats.total_points_earned || 0}
            </div>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>Puntos Totales</p>
          </div>
        </motion.div>
      )}

      {/* Exámenes disponibles */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            <BookOpen style={{ display: 'inline', marginRight: '10px' }} />
            Exámenes Disponibles
          </h2>
          
          <Link to="/placement/test" className="btn btn-success">
            <Target style={{ marginRight: '8px' }} />
            Examen de Ubicación
          </Link>
        </div>

        {error && (
          <div className="alert alert-error">
            😔 {error}
          </div>
        )}

        {exams.length === 0 ? (
          <div className="card" style={{ textAlign: 'center' }}>
            <h3>📚 No hay exámenes disponibles</h3>
            <p>¡Vuelve pronto para nuevas aventuras en inglés!</p>
          </div>
        ) : (
          <div className="grid grid-2">
            {exams.map((exam, index) => {
              const status = getExamStatus(exam);
              return (
                <motion.div
                  key={exam.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="card interactive"
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                    <h3 style={{ color: '#333', margin: 0 }}>{exam.title}</h3>
                    <span style={{ 
                      background: status.color, 
                      color: 'white', 
                      padding: '5px 10px', 
                      borderRadius: '15px', 
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {status.icon} {status.text}
                    </span>
                  </div>

                  <p style={{ color: '#666', marginBottom: '15px' }}>
                    {exam.description || 'Demuestra tus conocimientos en inglés'}
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <Clock style={{ color: '#4ecdc4', marginBottom: '5px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{exam.duration_minutes} min</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Duración</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <Target style={{ color: '#ff6b6b', marginBottom: '5px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{exam.question_count}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Preguntas</div>
                    </div>
                  </div>

                  {exam.deadline && (
                    <div style={{ 
                      background: 'rgba(255, 107, 107, 0.1)', 
                      padding: '10px', 
                      borderRadius: '10px', 
                      marginBottom: '15px',
                      textAlign: 'center'
                    }}>
                      <Calendar style={{ display: 'inline', marginRight: '5px', color: '#ff6b6b' }} />
                      <span style={{ fontSize: '14px', color: '#ff6b6b', fontWeight: 'bold' }}>
                        Fecha límite: {formatDate(exam.deadline)}
                      </span>
                    </div>
                  )}

                  {exam.canAttempt ? (
                    <Link 
                      to={`/exam/${exam.id}/instructions`} 
                      className="btn"
                      style={{ width: '100%' }}
                    >
                      <BookOpen style={{ marginRight: '8px' }} />
                      ¡Empezar Examen! 🚀
                    </Link>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <button disabled className="btn btn-secondary" style={{ width: '100%' }}>
                        ✅ Completado
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;
